import ENDPOINTS from '@/config/http/endpoints'
import { rootApi } from '@/ducks/root.api'
import {
  ApiFetchAllStoreBranches,
  ApiFetchStoreBranches,
  ApiGetStoreAvailability,
  ApiGetStoreAutoAcceptStatus,
  ApiUpdateStoreAutoAcceptStatus,
  ApiGetStoreBusyStatus,
  ApiUpdateStoreBusyStatus,
  ApiGetStoreProfile,
  StoreMarketingProfile
} from '@/ducks/store/store.types'

export const storeApi = rootApi.injectEndpoints({
  endpoints: build => ({
    changeStoreAvailability: build.mutation<
      { message: string },
      {
        store_id: string
        availability: number
      }
    >({
      query: data => ({
        method: 'POST',
        url: ENDPOINTS.TOGGLE_AVAILABILITY,
        data
      })
    }),
    // A read, despite the POST: declared as a query so RTK Query dedupes it
    // (as a mutation, concurrent boots each fired their own request).
    fetchStoreProfile: build.query<
      ApiGetStoreProfile,
      {
        store_id: string
      }
    >({
      query: data => ({
        method: 'POST',
        url: ENDPOINTS.GET_STORE_INFO,
        data,
        retryable: true
      })
    }),
    getStoreMarketingProfile: build.query<StoreMarketingProfile, void>({
      query: () => ({
        method: 'GET',
        url: ENDPOINTS.GET_STORE_PROFILE_V31
      }),
      transformResponse: (response: {
        profileData?: { store_link?: string; unique_code?: string }
      }): StoreMarketingProfile => ({
        storeLink: response?.profileData?.store_link?.trim() || null,
        uniqueCode: response?.profileData?.unique_code?.trim() || null
      })
    }),
    fetchStoreAvailability: build.query<
      ApiGetStoreAvailability,
      {
        store_id: string
      }
    >({
      query: ({ store_id }) => ({
        method: 'GET',
        url: ENDPOINTS.FETCH_AVAILABILITY(store_id)
      })
    }),
    fetchStoreBranches: build.query<ApiFetchStoreBranches, void>({
      providesTags: [{ type: 'Menu', id: 'LIST' }],
      query: () => ({
        method: 'GET',
        url: ENDPOINTS.GET_STORE_BRANCHES
      })
    }),
    fetchAllStoreBranches: build.query<ApiFetchAllStoreBranches, void>({
      providesTags: [{ type: 'Menu', id: 'LIST' }],
      query: () => ({
        method: 'GET',
        url: ENDPOINTS.GET_ALL_STORE_BRANCHES
      })
    }),
    setBranchAvailability: build.mutation<
      { message: string },
      { store_id: string; availability: number }
    >({
      query: (data) => ({
        method: 'POST',
        url: ENDPOINTS.SET_BRANCH_AVAILABILITY,
        data
      }),
      invalidatesTags: [{ type: 'Menu', id: 'LIST' }]
    }),
    getStoreBusyStatus: build.query<ApiGetStoreBusyStatus, void>({
      query: () => ({
        method: 'GET',
        url: ENDPOINTS.GET_STORE_BUSY_STATUS
      })
    }),
    updateStoreBusyStatus: build.mutation<
      { message: string },
      ApiUpdateStoreBusyStatus
    >({
      query: (data) => ({
        method: 'PATCH',
        url: ENDPOINTS.UPDATE_STORE_BUSY_STATUS,
        data
      })
    }),
    getStoreAutoAcceptStatus: build.query<ApiGetStoreAutoAcceptStatus, void>({
      providesTags: [{ type: 'Store', id: 'AUTO_ACCEPT' }],
      query: () => ({
        method: 'GET',
        url: ENDPOINTS.GET_STORE_AUTO_ACCEPT_STATUS,
        ignoreAuthRedirectOn403: true
      })
    }),
    updateStoreAutoAcceptStatus: build.mutation<
      { message?: string },
      ApiUpdateStoreAutoAcceptStatus
    >({
      invalidatesTags: [{ type: 'Store', id: 'AUTO_ACCEPT' }],
      query: data => ({
        method: 'PATCH',
        url: ENDPOINTS.UPDATE_STORE_AUTO_ACCEPT_STATUS,
        data
      })
    })
  })
})

export const {
  useChangeStoreAvailabilityMutation,
  useFetchStoreProfileQuery,
  useGetStoreMarketingProfileQuery,
  useFetchStoreAvailabilityQuery,
  useFetchStoreBranchesQuery,
  useFetchAllStoreBranchesQuery,
  useSetBranchAvailabilityMutation,
  useGetStoreBusyStatusQuery,
  useUpdateStoreBusyStatusMutation,
  useGetStoreAutoAcceptStatusQuery,
  useUpdateStoreAutoAcceptStatusMutation
} = storeApi

export default storeApi
